import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? "http://localhost:3000";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { LanguageSelector } from "../components/LanguageSelector.js";

export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const signup = trpc.auth.signup.useMutation({
    onSuccess: async () => {
      await utils.auth.me.fetch();
      navigate(from, { replace: true });
    },
    onError: (err) => {
      setFieldError(err.message);
    },
  });

  return (
    <div className="min-h-screen flex" style={{ background: "var(--color-background)" }}>
      {/* Left dark panel */}
      <div
        className="hidden md:flex md:w-96 lg:w-[28rem] flex-col justify-between p-10 flex-shrink-0"
        style={{ background: "var(--color-foreground)" }}
      >
        <Link
          to="/"
          className="font-serif text-xl font-semibold tracking-tight"
          style={{ color: "var(--color-background)", textDecoration: "none" }}
        >
          {t("nav.logo")}
        </Link>
        <div>
          <blockquote
            className="font-serif italic text-2xl leading-relaxed mb-4"
            style={{ color: "oklch(82% 0.012 80)" }}
          >
            {t("auth.signup.quote")}
          </blockquote>
          <cite className="text-xs not-italic" style={{ color: "oklch(55% 0.01 75)" }}>
            {t("auth.signup.quoteAuthor")}
          </cite>
        </div>
        <p className="text-xs" style={{ color: "oklch(48% 0.01 75)" }}>
          {t("auth.signup.hasAccount")}{" "}
          <Link to="/login" className="underline" style={{ color: "oklch(70% 0.08 60)" }}>
            {t("auth.signup.signInLink")}
          </Link>
        </p>
      </div>

      {/* Right form */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-6 py-12"
        style={{ animation: "fade-in 0.4s ease-out both" }}
      >
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <Link
          to="/"
          className="md:hidden font-serif text-xl font-semibold mb-10 block"
          style={{ color: "var(--color-foreground)", textDecoration: "none" }}
        >
          {t("nav.logo")}
        </Link>

        <div
          className="w-full max-w-sm"
          style={{ animation: "slide-up 0.45s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}
        >
          <div className="mb-8">
            <h1
              className="font-serif font-semibold mb-1"
              style={{ fontSize: "1.875rem", letterSpacing: "-0.02em", color: "var(--color-foreground)" }}
            >
              {t("auth.signup.title")}
            </h1>
            <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
              {t("auth.signup.subtitle")}
            </p>
          </div>

          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              setFieldError(null);
              signup.mutate({ displayName, email, password });
            }}
          >
            <Input
              label={t("auth.signup.displayNameLabel")}
              type="text"
              autoComplete="name"
              required
              minLength={1}
              maxLength={64}
              placeholder={t("auth.signup.displayNamePlaceholder")}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Input
              label={t("auth.signup.emailLabel")}
              type="email"
              autoComplete="email"
              required
              placeholder={t("auth.signup.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label={t("auth.signup.passwordLabel")}
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder={t("auth.signup.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldError ?? undefined}
              hint={t("auth.signup.passwordHint")}
            />
            <Button type="submit" loading={signup.isPending} className="w-full" size="lg">
              {t("auth.signup.submit")}
            </Button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
            <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>{t("common.or")}</span>
            <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
          </div>

          <a
            href={`${SERVER_URL}/auth/google`}
            className="mt-4 flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
            style={{ border: "1px solid var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", textDecoration: "none" }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {t("auth.signup.googleButton")}
          </a>

          <p className="mt-6 text-center text-sm md:hidden" style={{ color: "var(--color-muted-foreground)" }}>
            {t("auth.signup.hasAccount")}{" "}
            <Link to="/login" style={{ color: "var(--color-accent)" }}>{t("auth.signup.signInLink")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
